import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Select, Chip, Alert, PanelCard, Spinner } from '@glplus/glpweb-dsl';
import {
  RECIPIENT_EMAIL_TYPES,
  vendorApiClient,
  type LocationRecipient,
  type LocationRecipients as Recipients,
  type RecipientEmailType,
  type RecipientInput,
  type TeamMember,
} from '../../lib/vendorApiClient';
import { describeError } from './LocationAdd';

const HEADING = '#303A42';
const MUTED = '#5A7063';
const BORDER = '#E5E5E5';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function memberName(m: TeamMember): string {
  const name = `${m.givenName ?? ''} ${m.familyName ?? ''}`.trim();
  return name || m.email || '';
}

export function toInput(people: LocationRecipient[]): RecipientInput[] {
  return people.map((p) => (p.vendorUserId ? { vendorUserId: p.vendorUserId } : { email: p.email, ...(p.name ? { name: p.name } : {}) }));
}

/** Who gets each kind of email about one location: up to a few people per kind, chosen from the team or by address. */
export default function LocationRecipients({ vendorId, storeId, canManage }: { vendorId: string; storeId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const [data, setData] = useState<Recipients | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyType, setBusyType] = useState<RecipientEmailType | null>(null);
  const [error, setError] = useState<{ type: RecipientEmailType; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    vendorApiClient
      .getLocationRecipients(vendorId, storeId)
      .then((r) => alive && setData(r))
      .catch((e: unknown) => alive && setLoadError(describeError(e, t)));
    vendorApiClient
      .listTeam(vendorId)
      .then((r) => alive && setTeam(r.items.filter((m) => m.vendorUserId && m.email)))
      .catch(() => alive && setTeam([]));
    return () => {
      alive = false;
    };
  }, [vendorId, storeId, t]);

  const save = async (type: RecipientEmailType, people: RecipientInput[]) => {
    if (!data) return;
    setBusyType(type);
    setError(null);
    try {
      const saved = await vendorApiClient.saveLocationRecipients(vendorId, storeId, type, people);
      setData({ ...data, types: { ...data.types, [type]: saved.people.map((p) => ({ ...p, emailType: type })) } });
    } catch (e) {
      setError({ type, text: describeError(e, t) });
    } finally {
      setBusyType(null);
    }
  };

  return (
    <Box data-testid="location-recipients">
      <PanelCard title={t('locations.recipients.title')} sx={{ height: 'auto', bgcolor: '#fff' }} headingLevel="h3">
        <Typography sx={{ fontSize: 14, color: MUTED }}>{t('locations.recipients.fallback')}</Typography>
        {loadError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {loadError}
          </Alert>
        )}
        {!data && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Spinner color="brand" />
          </Box>
        )}
        {data &&
          RECIPIENT_EMAIL_TYPES.map((type) => (
            <RecipientType
              key={type}
              type={type}
              people={data.types[type] ?? []}
              max={data.maxPerType}
              team={team}
              canManage={canManage}
              busy={busyType === type}
              error={error?.type === type ? error.text : null}
              onSave={(people) => void save(type, people)}
            />
          ))}
      </PanelCard>
    </Box>
  );
}

function RecipientType({
  type,
  people,
  max,
  team,
  canManage,
  busy,
  error,
  onSave,
}: {
  type: RecipientEmailType;
  people: LocationRecipient[];
  max: number;
  team: TeamMember[];
  canManage: boolean;
  busy: boolean;
  error: string | null;
  onSave: (people: RecipientInput[]) => void;
}) {
  const { t } = useTranslation();
  const [member, setMember] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const full = people.length >= max;
  const listed = new Set(people.map((p) => p.email.toLowerCase()));
  const choices = team.filter((m) => !listed.has((m.email ?? '').toLowerCase()));
  const current = toInput(people);

  const addMember = () => {
    if (!member) return;
    onSave([...current, { vendorUserId: member }]);
    setMember('');
  };

  const addEmail = () => {
    const typed = email.trim().toLowerCase();
    if (!EMAIL.test(typed)) return setProblem(t('locations.recipients.emailInvalid'));
    if (listed.has(typed)) return setProblem(t('locations.recipients.duplicate'));
    setProblem(null);
    onSave([...current, { email: typed, ...(name.trim() ? { name: name.trim() } : {}) }]);
    setEmail('');
    setName('');
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER}` }} data-testid={`location-recipients-${type}`}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{t(`locations.recipients.types.${type}.name`)}</Typography>
      <Typography sx={{ fontSize: 13, color: MUTED }}>{t(`locations.recipients.types.${type}.about`)}</Typography>

      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {people.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: MUTED }}>{t('locations.recipients.nobody')}</Typography>
        ) : (
          people.map((p, i) => (
            <Box key={p.email} data-testid={`location-recipient-${type}-${i}`}>
              <Chip
                label={p.name ? `${p.name} · ${p.email}` : p.email}
                size="small"
                backgroundColor="#F1F2F4"
                textColor="#3C4650"
                fontSize={13}
                disabled={busy}
                aria-label={t('locations.recipients.removeAria', { person: p.name || p.email })}
                onDelete={canManage ? () => onSave(current.filter((_, j) => j !== i)) : undefined}
              />
            </Box>
          ))
        )}
      </Box>

      {canManage && !full && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {choices.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 220px', maxWidth: 320 }} data-testid={`location-recipients-member-${type}`}>
                <Select
                  ariaLabel={t('locations.recipients.teamMember')}
                  label={t('locations.recipients.teamMember')}
                  placeholder={t('locations.recipients.chooseMember')}
                  value={member}
                  onChange={(_e, v) => setMember(v)}
                  options={choices.map((m) => ({ value: m.vendorUserId as string, label: memberName(m) }))}
                  disabled={busy}
                  size="small"
                  fullWidth
                />
              </Box>
              <Button
                ariaLabel={t('locations.recipients.addMember')}
                buttonText={t('locations.recipients.add')}
                variant="outlinedGreen"
                disabled={busy || !member}
                onClick={addMember}
                dataTestId={`location-recipients-add-member-${type}`}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
              <TextField
                ariaLabel={t('locations.recipients.email')}
                label={t('locations.recipients.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                error={!!problem}
                errorText={problem ?? undefined}
                disabled={busy}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { maxLength: 320 } }}
                dataTestId={`location-recipients-email-${type}`}
              />
            </Box>
            <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
              <TextField
                ariaLabel={t('locations.recipients.name')}
                label={t('locations.recipients.name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                size="small"
                fullWidth
                slotProps={{ htmlInput: { maxLength: 120 } }}
                dataTestId={`location-recipients-name-${type}`}
              />
            </Box>
            <Button
              ariaLabel={t('locations.recipients.addEmail')}
              buttonText={t('locations.recipients.add')}
              variant="outlinedGreen"
              disabled={busy || !email.trim()}
              onClick={addEmail}
              dataTestId={`location-recipients-add-email-${type}`}
            />
          </Box>
        </Box>
      )}
      {canManage && full && <Typography sx={{ mt: 1, fontSize: 13, color: MUTED }}>{t('locations.recipients.full', { count: max })}</Typography>}
      {error && (
        <Typography role="alert" sx={{ mt: 1, fontSize: 13, fontWeight: 700, color: '#C62828' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
